// Thin wrapper over the Umami tracker for MarkFlow funnel events (MF-203).
//
// The tracker is injected only in production builds (vite.config.ts) and only
// starts once the user grants `analytics` consent (src/consent/consent-activator
// promotes the parked <script>). So `window.umami` is:
//   - always undefined in dev,
//   - undefined in prod until consent is granted AND the async script has loaded.
//
// A naive `window.umami?.track()` would silently drop events fired in that race
// window — most importantly `signup_complete`, which fires right after the OAuth
// redirect, before the just-activated script has finished loading. So events are
// buffered and flushed once the tracker appears. If consent is never granted the
// buffer simply never flushes (bounded poll gives up) — a safe no-op.

declare global {
  interface Window {
    umami?: { track: (event: string, data?: Record<string, unknown>) => void };
  }
}

type Pending = [event: string, data: Record<string, unknown> | undefined];

const buffer: Pending[] = [];
let polling = false;
let attempts = 0;

function flush(): void {
  if (typeof window === 'undefined' || !window.umami?.track) return;
  while (buffer.length) {
    const [event, data] = buffer.shift()!;
    window.umami.track(event, data);
  }
}

function poll(): void {
  if (polling) return;
  polling = true;
  const tick = () => {
    attempts += 1;
    if (typeof window !== 'undefined' && window.umami?.track) {
      flush();
      polling = false;
      return;
    }
    if (attempts > 40) {
      // ~20s with no tracker → consent almost certainly withheld. Give up so we
      // don't poll forever; a later trackEvent() restarts the window.
      polling = false;
      return;
    }
    setTimeout(tick, 500);
  };
  setTimeout(tick, 500);
}

// Fire a Umami custom event. Safe no-op when analytics is unavailable; buffers
// through the post-consent script-load race.
export function trackEvent(event: string, data?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  if (window.umami?.track) {
    window.umami.track(event, data);
    return;
  }
  buffer.push([event, data]);
  attempts = 0; // restart the poll window for this fresh event
  poll();
}

// Fire `event` only the first time `storageKey` is seen in this browser. Used for
// activation signals like `first_bookmark` where we want the milestone once, not
// on every bookmark add. Best-effort: falls back to firing if localStorage is
// unavailable (private mode).
export function trackEventOnce(event: string, storageKey: string, data?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  try {
    if (localStorage.getItem(storageKey)) return;
    localStorage.setItem(storageKey, '1');
  } catch {
    // localStorage blocked — fall through and track anyway.
  }
  trackEvent(event, data);
}
